import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { EVENT_ERROR, EVENT_EXPIRED, OktaAuth } from '@okta/okta-auth-js';
import { NGXLogger } from 'ngx-logger';

/**
 * This service is responsible for monitoring and handling token expiration.
 * Note that OktaAuthStateService has an authState$ observable of type Observable<AuthState>
 * you can subscribe to changes in the accessToken, idToken, refreshToken, isAuthenticated,
 * or error states. The one thing that you don't get is the reason why the state changed.
 * E.g., if isAuthenticated changes to false, you don't know if there was an error renewing
 * a token, a token expiring, or the user intentionally signing out. It may not matter
 * and we may want to utilize the observable. But for now, I went with the event handlers so
 * that I can stay out of the loop on users intentionally signing out and giving the
 * option in the future to do something different on errors.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthExpirationService {
    private oktaAuth: OktaAuth = inject(OKTA_AUTH);
    private router: Router = inject(Router);
    private logger: NGXLogger = inject(NGXLogger);

    // Using this.oktaAuth.signInWithRedirect() left a race condition where you can get a PKCE error
    // when multiple auth flows wind up executing concurrently, even guarding with a variable in this component
    // and checking this.oktaAuth.isLoginRedirect(). Troubleshooting with Claude, it seems that OktaAuthGuard
    // can also trigger a call to signInWithRedirect, causing the race conditions on expected PKCE values.
    // Also this.oktaAuth.signOut() is very harsh for the user. A simple redirect to /home avoided this, and Claude
    // actually made the same suggestion.
    private handleExpiration() {
        // Debounce: handle once even though both tokens expire
        if (
            this.oktaAuth.isLoginRedirect() ||
            this.router.url === '/home' ||
            this.router.url.startsWith('/login/callback')
        ) {
            return;
        }
        this.router.navigate(['/home']);
    }

    constructor() {
        // We're ignoring EVENT_REMOVED (user intentionally signed out) right now
        // EVENT_SET_STORAGE is not relevant
        // We do want to take action if the token expired
        this.oktaAuth.tokenManager.on(EVENT_EXPIRED, (key, _) => {
            if (key === 'accessToken') {
                this.logger.debug(`Token ${key} expired. Redirecting to home...`);
                this.handleExpiration();
            }
        });

        // This covers:
        //   - Tokens never existed (OAuth errors)
        //   - Tokens already expired when renewal attempted
        //   - Token validation failures
        //   - Network failures preventing renewal
        this.oktaAuth.tokenManager.on(EVENT_ERROR, (error) => {
            this.logger.debug('Token manager error:', error);
            this.handleExpiration();
        });
    }
}
